// HTTP client for the DiscoveryRun REST endpoints.
//
// Mounted at `/equipment_discovery/` per the platform plugin convention
// (no `/core/api/` prefix — see services/services/api/urls.py lines 101-108
// for the auto-include pattern).
//
// Used by:
//   - `useDiscoveryRun(runId)`        polling hook for DiscoveryPanel
//   - `DiscoveryPanel`                master-flow inline panel inside jetson card
//   - `AddDeviceDropdown`             create targeted run for single-device add
//   - finalizer's per-card useDiscover (when invoked via runs endpoint)
//
// Wire-format types come from `./discovery.ts` (finalizer-owned).

import { axiosInstance } from './api';
import type {
  TargetSpec,
  DiscoveryEvent,
  RunStatus,
  Credentials,
} from './discovery';

const PREFIX = '/equipment_discovery';

// ---- DTOs ----------------------------------------------------------

export type DiscoveryRunKind = 'master' | 'targeted';

export type DiscoveryRunStatus =
  | 'created'
  | 'running'
  | 'paused_on_creds'
  | RunStatus;          // 'ok' | 'partial' | 'failed' | 'cancelled'

export interface DiscoveryRunSummary {
  id: string;
  local_computer: string;
  zone: number;
  kind: DiscoveryRunKind;
  status: DiscoveryRunStatus;
  target_spec: TargetSpec;
  started_at: string;
  finished_at: string | null;
  summary: Record<string, unknown> | null;
}

export interface DiscoveryRunDetail extends DiscoveryRunSummary {
  events: DiscoveryEvent[];
}

export interface CreateDiscoveryRunInput {
  local_computer: string;
  zone: number;
  kind: DiscoveryRunKind;
  target_spec: TargetSpec;
  /** Optional initial creds — saves a round-trip if user knows the password. */
  creds?: Credentials;
}

export type ResumeAction =
  | { action: 'try'; candidates: Credentials[] }
  | { action: 'skip' };

export interface DiscoveryAllowedResponse {
  allowed: boolean;
  reason: string | null;
}

// ---- endpoints -----------------------------------------------------

/** Create a DiscoveryRun + dispatch the celery worker. */
export const createDiscoveryRun = (input: CreateDiscoveryRunInput) =>
  axiosInstance.post<DiscoveryRunSummary>(`${PREFIX}/discovery-runs/`, input);

/** Poll the run — returns full event log + status. Stop polling on terminal. */
export const getDiscoveryRun = (runId: string) =>
  axiosInstance.get<DiscoveryRunDetail>(`${PREFIX}/discovery-runs/${runId}/`);

/** Respond to a credential_needed pause. */
export const resumeDiscoveryRun = (runId: string, action: ResumeAction) =>
  axiosInstance.post<{ detail: string; id: string }>(
    `${PREFIX}/discovery-runs/${runId}/resume/`,
    action,
  );

/** Request cancellation. Already-persisted devices remain. */
export const cancelDiscoveryRun = (runId: string) =>
  axiosInstance.post<DiscoveryRunSummary>(
    `${PREFIX}/discovery-runs/${runId}/cancel/`,
  );

/** List recent runs for a zone. ?active=1 filters to non-terminal. */
export const listZoneDiscoveryRuns = (
  zoneId: number | string,
  opts: { active?: boolean; limit?: number } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.active) qs.set('active', '1');
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  return axiosInstance.get<{ results: DiscoveryRunSummary[] }>(
    `${PREFIX}/zones/${zoneId}/discovery-runs/${tail}`,
  );
};

/**
 * Defense-in-depth gate check. In normal operation always returns
 * `{ allowed: true }` because the binding-endpoint invariant guarantees
 * any bound LC is fully set up (per design doc §0 Q4). FE uses this for
 * disabled-with-tooltip rendering in the never-should-happen case.
 */
export const getDiscoveryAllowed = (lcId: string) =>
  axiosInstance.get<DiscoveryAllowedResponse>(
    `${PREFIX}/local-computers/${lcId}/discovery-allowed/`,
  );

// ---- helpers -------------------------------------------------------

/** Terminal statuses — polling stops, summary becomes available. */
export const TERMINAL_STATUSES: readonly DiscoveryRunStatus[] = [
  'ok',
  'partial',
  'failed',
  'cancelled',
] as const;

export const isTerminal = (status: DiscoveryRunStatus): boolean =>
  TERMINAL_STATUSES.includes(status);

/** True while the FE should keep polling (created/running/paused_on_creds). */
export const isLive = (status: DiscoveryRunStatus): boolean =>
  !isTerminal(status);
